import { createRouter } from "./lib/router";
import { wireUnsavedGuard } from "./lib/unsavedGuard";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/components.css";
import { flushPendingToast } from "./lib/toast";
import { fetchMe } from "./services/auth";
import { auditLogView } from "./views/audit-log";
import { bankAccountDetailView, bankAccountsListView } from "./views/bank-accounts";
import { bankProfileDetailView, bankProfilesListView } from "./views/bank-profiles";
import { bankingView } from "./views/banking";
import { bankingImportView } from "./views/banking-import";
import { bankingReconciliationView } from "./views/banking-reconciliation";
import { bankingTransfersView } from "./views/banking-transfers";
import {
  categoriesListView,
  categoryDetailView,
  categoryRuleDetailView,
  categoryRulesListView,
} from "./views/categories";
import { clientDetailView, clientsListView } from "./views/clients";
import { companiesListView, companyDetailView } from "./views/companies";
import { homeView } from "./views/home";
import { invoiceEditorView, invoicesListView, issuedInvoiceView } from "./views/invoices";
import { invoicesImportView } from "./views/invoices-import";
import { loanDetailView, loansListView } from "./views/loans";
import { loginView } from "./views/login";
import { notFoundView } from "./views/not-found";
import { projectDetailView } from "./views/project";
import { reportsView } from "./views/reports";
import { reportsAgingView } from "./views/reports-aging";
import { reportsIncomeByClientView } from "./views/reports-income-by-client";
import { backupsView } from "./views/backups";
import { settingsView } from "./views/settings";
import { buildShell } from "./views/shell";
import { timesheetsView } from "./views/timesheets";
import { vatView } from "./views/vat";
import { yearEndView } from "./views/year-end";

const appRootOrNull = document.querySelector<HTMLDivElement>("#app");
if (!appRootOrNull) throw new Error("missing #app mount point");
const appRoot: HTMLDivElement = appRootOrNull;

async function bootstrap(): Promise<void> {
  flushPendingToast();

  if (location.pathname === "/login") {
    const router = createRouter();
    router.register("/login", loginView);
    router.start(appRoot);
    return;
  }

  try {
    await fetchMe();
  } catch {
    // fetchMe's underlying request already redirects to /login on 401.
    return;
  }

  const { shell, mainOutlet } = buildShell();
  // Drops index.html's pre-boot placeholder. appendChild alone would leave it stacked
  // above the shell.
  appRoot.replaceChildren();
  appRoot.appendChild(shell);

  const router = createRouter();
  wireUnsavedGuard(router);
  router.register("/", homeView);
  router.register("/timesheets", timesheetsView);
  router.register("/companies", companiesListView);
  router.register("/companies/:id", companyDetailView);
  router.register("/clients", clientsListView);
  router.register("/clients/:id", clientDetailView);
  router.register("/projects/:id", projectDetailView);
  router.register("/invoices", invoicesListView);
  // Before /invoices/:id, or "import" is matched as an invoice id.
  router.register("/invoices/import", invoicesImportView);
  router.register("/invoices/:id/view", issuedInvoiceView);
  router.register("/invoices/:id", invoiceEditorView);
  router.register("/vat", vatView);
  router.register("/reports", reportsView);
  router.register("/reports/aging", reportsAgingView);
  router.register("/reports/income-by-client", reportsIncomeByClientView);
  router.register("/year-end", yearEndView);
  router.register("/banking", bankingView);
  router.register("/banking/import", bankingImportView);
  router.register("/banking/reconciliation", bankingReconciliationView);
  router.register("/banking/transfers", bankingTransfersView);
  router.register("/loans", loansListView);
  router.register("/loans/:id", loanDetailView);
  router.register("/categories", categoriesListView);
  router.register("/categories/:id", categoryDetailView);
  router.register("/category-rules", categoryRulesListView);
  router.register("/category-rules/:id", categoryRuleDetailView);
  router.register("/bank-profiles", bankProfilesListView);
  router.register("/bank-profiles/:id", bankProfileDetailView);
  router.register("/bank-accounts", bankAccountsListView);
  router.register("/bank-accounts/:id", bankAccountDetailView);
  router.register("/settings", settingsView);
  router.register("/backups", backupsView);
  router.register("/audit-log", auditLogView);
  router.notFound(notFoundView);
  router.start(mainOutlet);
}

void bootstrap();
